/**
 * The connection indicator in the control bar, computed from what is actually
 * happening rather than from `Math.random()`.
 *
 * It is worth being blunt about what this replaces: the previous implementation
 * seeded `{bitrate: 1200, packetLoss: 0.1, latency: 45}` and then invented new
 * plausible numbers on a timer, with a comment saying "Simulate network quality
 * changes". It fed the always-visible control bar, not a debug panel. Every
 * user in every call was shown a connection reading with no connection in it.
 *
 * Source is the local peer's own send side, which is already registered and
 * polled — `local:camera:audio` and `local:camera:video`. Loss and round-trip
 * come from the far end's receiver reports (`remote-inbound-rtp`), because a
 * sender's own counters cannot see what failed to arrive.
 *
 * The one rule that matters here: when there is no measurement, this reports
 * `'unknown'` and nulls. It never fills the gap with something plausible. A
 * blank indicator is honest; an invented one is what we just removed.
 */
import { rtpStats, localStatsKey, LOCAL_STATS_KEYS, type RtpSnapshot } from './rtp-stats'

export type ConnectionQuality = 'excellent' | 'good' | 'fair' | 'poor' | 'offline' | 'unknown'

export type ConnectionStats = {
  /** Total send bitrate, kbps. Null until measured. */
  bitrate: number | null
  /** Percent, 0-100, as reported by the receiver. Null until measured. */
  packetLoss: number | null
  /** Round-trip in ms. Null until measured. */
  latency: number | null
  /** Jitter in ms. Null until measured. */
  jitter: number | null
  quality: ConnectionQuality
}

export const UNKNOWN: ConnectionStats = {
  bitrate: null, packetLoss: null, latency: null, jitter: null, quality: 'unknown',
}

/**
 * Loss and latency decide it, worst-of wins.
 *
 * Thresholds are deliberately generous at the top: a call at 2% loss is
 * unpleasant but usable, and an indicator that shows trouble during normal
 * operation trains people to ignore it.
 */
export function qualityFrom(lossPct: number | null, latencyMs: number | null): ConnectionQuality {
  if (lossPct === null && latencyMs === null) return 'unknown'
  const loss = lossPct ?? 0
  const rtt = latencyMs ?? 0

  // Nothing is arriving. Distinct from 'poor', which is still a call.
  if (loss >= 25) return 'offline'

  const byLoss: ConnectionQuality =
    loss < 0.5 ? 'excellent' : loss < 2 ? 'good' : loss < 5 ? 'fair' : 'poor'
  const byRtt: ConnectionQuality =
    rtt < 150 ? 'excellent' : rtt < 250 ? 'good' : rtt < 400 ? 'fair' : 'poor'

  const rank: ConnectionQuality[] = ['excellent', 'good', 'fair', 'poor']
  return rank[Math.max(rank.indexOf(byLoss), rank.indexOf(byRtt))]
}

/** Fold the audio and video send snapshots into one reading. */
export function combine(
  audio: RtpSnapshot | null | undefined,
  video: RtpSnapshot | null | undefined,
): ConnectionStats {
  if (!audio && !video) return UNKNOWN

  const kbps = (audio?.kbps ?? 0) + (video?.kbps ?? 0)
  const bitrate = audio?.kbps === undefined && video?.kbps === undefined ? null : Math.round(kbps)

  // Worst of the two streams, not the average: audio surviving does not make a
  // failing video stream acceptable.
  const losses = [audio?.fractionLost, video?.fractionLost].filter((v): v is number => typeof v === 'number')
  const rtts = [video?.roundTripMs, audio?.roundTripMs].filter((v): v is number => typeof v === 'number')
  const jits = [video?.jitterMs, audio?.jitterMs].filter((v): v is number => typeof v === 'number')

  const packetLoss = losses.length ? Math.round(Math.max(...losses) * 1000) / 10 : null
  const latency = rtts.length ? Math.max(...rtts) : null
  const jitter = jits.length ? Math.max(...jits) : null

  return { bitrate, packetLoss, latency, jitter, quality: qualityFrom(packetLoss, latency) }
}

/**
 * Watch the local send side. Returns an unsubscribe.
 *
 * Two subscriptions, one per kind, because that is how rtp-stats is keyed. The
 * poller only walks ids that have listeners, so this costs two `getStats()`
 * calls every 2s for as long as the indicator is on screen.
 */
export function subscribeConnectionQuality(
  onChange: (stats: ConnectionStats) => void,
): () => void {
  let audio: RtpSnapshot | null = null
  let video: RtpSnapshot | null = null
  const emit = () => onChange(combine(audio, video))

  const offA = rtpStats.subscribe(localStatsKey('audio'), (s) => { audio = s; emit() })
  const offV = rtpStats.subscribe(localStatsKey('video'), (s) => { video = s; emit() })

  warnOnUnwatchedLocalKeys()

  return () => { offA(); offV() }
}

let warned = false
/**
 * If something registers local send-side stats under a key this module does not
 * watch, the indicator silently under-reports rather than failing. Say so.
 *
 * This is not hypothetical: the screen-share and extra-camera producers were
 * registered exactly that way, and nothing noticed for months.
 */
function warnOnUnwatchedLocalKeys(): void {
  if (warned) return
  warned = true
  const unwatched = rtpStats.ids().filter(
    (id) => id.startsWith('local:') && !(LOCAL_STATS_KEYS as readonly string[]).includes(id),
  )
  if (unwatched.length) {
    console.warn(
      '[connection-quality] local send stats exist under keys the indicator does not watch — ' +
      'it is not counting them: ' + unwatched.join(', '),
    )
  }
}

/** Test seam. */
export function __resetWarning(): void { warned = false }
